import SlotGridCard from './SlotGridCard/SlotGridCard';
import { AppSettings } from '@opanuj/shared/types';
import { Box, Center, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import { daysOrder, formatWeekday } from '@opanuj/shared/utils';
import { findSlotAvailabilityIssue } from '@opanuj/shared/slots';
import { Fragment } from 'react';

const sortByTime = (a: string, b: string) => {
	const [aHour = '0', aMinute = '0'] = a.split(':');
	const [bHour = '0', bMinute = '0'] = b.split(':');
	const aTotal = Number(aHour) * 60 + Number(aMinute);
	const bTotal = Number(bHour) * 60 + Number(bMinute);
	return aTotal - bTotal;
};

export function SlotsGridHeader({ days, activeTime }: { days: string[]; activeTime?: string | null }) {
	return (
		<Box position="sticky" top={0} zIndex={10} bg="white" pb={2} data-sticky-header>
			<Grid
				w="100%"
				minW={0}
				gap={2}
				templateColumns={`48px repeat(${days.length}, minmax(110px, 1fr))`}
			>
				<GridItem bg="gray.50" borderRadius="md" p={1.5}>
					<Text fontWeight="semibold" fontSize="sm" whiteSpace="nowrap">
						{activeTime ?? 'godz.'}
					</Text>
				</GridItem>
				{days.map((day) => (
					<GridItem key={day} bg="gray.50" borderRadius="md" p={1.5}>
						<Text fontWeight="semibold" fontSize="sm" lineHeight="short" whiteSpace="nowrap">
							{formatWeekday(day as any)}
						</Text>
					</GridItem>
				))}
			</Grid>
		</Box>
	);
}

function SlotsGrid({
	slots,
	settings,
	selectedSlot,
	selectedSlotId,
	onSelectSlot,
}: {
	slots: any[];
	settings: AppSettings;
	selectedSlot: any | null;
	selectedSlotId: string | null;
	onSelectSlot: (slotId: string | null) => void;
}) {
	const daysInData = daysOrder.filter((day) =>
		slots.some((slot) => slot?.day === day)
	);
	const days = daysInData.length > 0 ? daysInData : daysOrder.slice(0, 6);
	const times = Array.from(
		new Set(slots.map((slot) => slot?.time).filter(Boolean))
	).sort(sortByTime);

	if (!times.length) {
		return (
			<Center p={20}>
				<Text color="gray.500">Brak terminów do wyświetlenia.</Text>
			</Center>
		);
	}

	return (
		<Box overflowX="hidden" w="100%">
			<Grid
				w="100%"
				minW={0}
				gap={2}
				templateColumns={`48px repeat(${days.length}, minmax(110px, 1fr))`}
			>
				{times.map((time) => (
					<Fragment key={`row-${time}`}>
						<GridItem
							key={`time-${time}`}
							bg="gray.50"
							borderRadius="md"
							p={1}
							data-time-label={time}
						>
							<Text fontWeight="semibold" fontSize="sm" lineHeight="short" whiteSpace="nowrap">
								{time}
							</Text>
						</GridItem>
						{days.map((day) => {
							const slotItems = slots
								.filter((slot) => slot?.day === day && slot?.time === time)
								.sort((a, b) => String(a?.code).localeCompare(String(b?.code)));
							return (
								<GridItem
									key={`${day}-${time}`}
									bg="gray.50"
									borderRadius="md"
									minH="40px"
								>
									<VStack gap={0} align="stretch" p={1}>
										{slotItems.map((slot) => {
											const selectedConflictIssue =
												selectedSlot &&
												slot.id !== selectedSlot.id
													? findSlotAvailabilityIssue(selectedSlot, [], [slot])
													: undefined;

											return (
												<SlotGridCard
													key={slot.id}
													slot={slot}
													isSelected={slot.id === selectedSlotId}
													selectedConflictIssue={selectedConflictIssue}
													isConflictWithSelected={Boolean(selectedConflictIssue)}
													onSelect={() => onSelectSlot(slot.id)}
													onOpenActions={() => onSelectSlot(slot.id)}
												/>
											);
										})}
									</VStack>
								</GridItem>
							);
						})}
					</Fragment>
				))}
			</Grid>
		</Box>
	);
}

export default SlotsGrid;
